import type { RenderContext } from '@/lib/render/RenderContext';

// A `{location.<placeholder>}` price token embedded in the badge text — the
// same placeholder syntax other widgets use. The captured `<placeholder>` (e.g.
// `[price_99_9]`, or plain `price_99_9` if the brackets are ever dropped)
// matches a `price.placeholder` value on the location's price zones and is
// resolved to a formatted price via `resolvePrice`. The token is not required
// to be bracketed; whichever `{location.*}` token resolves to a price wins, so
// prose tokens such as `{location.name}` are ignored.
const PRICE_PLACEHOLDER_RE = /\{location\.([\w.[\]-]+)\}/g;

export interface HeroBadge {
  readonly prefix: string;
  readonly price: string;
  readonly unit: string;
}

/**
 * Resolves the hero badge from a `badgeText` template. The template may
 * contain a `{location.<placeholder>}` price token (e.g.
 * `{location.[price_99_9]}`) that maps to a specific `price.placeholder` on the
 * location's active price zone and resolves to a formatted price (e.g. "9,35 €")
 * via `resolvePrice`.
 *
 * The badge splits the text around the token into three tiers: prefix
 * (above), price (middle), unit (below). Prose around the token still runs
 * through the general placeholder/glossary `substitute` engine. If the
 * template contains no `{location.*}` token at all, the full (substituted) text
 * is shown as a single-line badge. If it carries `{location.*}` token(s) but
 * none of them resolves to a price, the badge is hidden (returns null).
 */
export function resolveHeroBadge(
  template: string | undefined,
  substitute: RenderContext['substitutePlaceholders'],
  resolvePrice: (placeholder: string) => string | null,
): HeroBadge | null {
  if (!template) {
    return null;
  }

  // The first `{location.*}` token that resolves to a price is the price tier;
  // other tokens (e.g. `{location.name}`) resolve to null and stay in the prose.
  const tokens = [...template.matchAll(PRICE_PLACEHOLDER_RE)];
  let priceMatch: { start: number; end: number; price: string } | null = null;
  for (const token of tokens) {
    const price = resolvePrice(token[1]);
    if (price != null) {
      const start = token.index ?? 0;
      priceMatch = { start, end: start + token[0].length, price };
      break;
    }
  }

  if (!priceMatch) {
    // A location token was present but no price resolved — hide the badge.
    if (tokens.length > 0) {
      return null;
    }
    // No token at all — run general substitution and show a single-line badge.
    const text = substitute(template).trim();
    return text ? { prefix: text, price: '', unit: '' } : null;
  }

  // Split the raw template around the token into prefix / unit prose.
  const prefix = substitute(template.slice(0, priceMatch.start)).trim();
  const unit = substitute(template.slice(priceMatch.end)).trim();

  return { prefix, price: priceMatch.price, unit };
}
